import { Modal } from "react-bootstrap";
import { useState, useMemo } from "react";
import API, { getloginUserId } from "../api/axios";
import { toast } from "react-toastify";
import TermsAndConditionModal from "./TermsAndConditionmodal";

export default function PostAdModal({ show, onHide }) {
  const [form, setForm] = useState({
    title: "",
    type: "",
    target_url: "",
    embed_code: "",
    placement: "",
    status: "inactive",
    added_by: "",
    email: "",
    ads_start_date: "",
    ads_end_date: "",
    budget: "",
  });

  const [image, setImage] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const today = useMemo(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  }, []);

  // Calculate minimum end date (either start date or today, whichever is later)
  const minEndDate = useMemo(() => {
    if (form.ads_start_date) {
      return form.ads_start_date;
    }
    return today;
  }, [form.ads_start_date, today]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If changing start date and it's after current end date, clear end date
    if (name === "ads_start_date" && form.ads_end_date && value > form.ads_end_date) {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        ads_end_date: ""
      }));
      toast.info("End date cleared as it was before the new start date");
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error("You must accept the Terms & Conditions before submitting");
      return;
    }

    // Validate dates
    if (form.ads_start_date && form.ads_end_date) {
      if (new Date(form.ads_start_date) > new Date(form.ads_end_date)) {
        toast.error("End date must be after start date");
        return;
      }
    }

    const formData = new FormData();
    Object.keys(form).forEach((key) => {
      formData.append(key, form[key]);
    });

    if (image) {
      formData.append("image_path", image);
    }

    formData.set("added_by", getloginUserId());
    formData.append("accepted_terms", true);

    try {
      const res = await API.post("/ad-slots", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(res.data.message || "Ad created successfully");

      // Reset form
      setForm({
        title: "",
        type: "",
        target_url: "",
        embed_code: "",
        placement: "",
        status: "inactive",
        added_by: "",
        email: "",
        ads_start_date: "",
        ads_end_date: "",
        budget: "",
      });
      setImage(null);
      setAcceptedTerms(false);
      onHide();
    } catch (err) {
      if (err.response?.data?.data) {
        Object.values(err.response.data.data).forEach((messages) => {
          messages.forEach((msg) => toast.error(msg));
        });
      } else {
        toast.error("Something went wrong");
      }
    }
  };

  const handleTermsClick = () => {
    setShowTermsModal(true);
  };

  const handleTermsAccept = () => {
    setAcceptedTerms(true);
    setShowTermsModal(false);
    toast.success("Terms & Conditions accepted");
  };

  const handleTermsModalHide = () => {
    setShowTermsModal(false);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Post Your Ad</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <input
            type="hidden"
            name="added_by"
            value={getloginUserId()}
            onChange={handleChange}
          />
          <Modal.Body>
            <div className="row g-3">
              {/* Title */}
              <div className="col-md-12">
                <label className="form-label">Ad Title *</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  maxLength={255}
                  required
                />
              </div>

              {/* Type */}
              <div className="col-md-6">
                <label className="form-label">Type *</label>
                <select
                  className="form-select"
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Type</option>
                  <option value="banner">Banner</option>
                  <option value="embedded">Embedded</option>
                </select>
              </div>

              {/* Placement */}
              <div className="col-md-6">
                <label className="form-label">Placement *</label>
                <select
                  className="form-select"
                  name="placement"
                  value={form.placement}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Placement</option>
                  <option value="sidebar">Sidebar</option>
                  <option value="leftsidebar">Left Sidebar</option>
                  <option value="rightsidebar">Right Sidebar</option>
                  <option value="leaderboard">Leaderboard</option>
                </select>
              </div>

              {/* Email */}
              <div className="col-md-6">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  required
                />
              </div>

              {/* Budget */}
              <div className="col-md-6">
                <label className="form-label">Budget *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  placeholder="Enter budget"
                  required
                />
              </div>

              {/* Start Date */}
              <div className="col-md-6">
                <label className="form-label">Start Date *</label>
                <input
                  type="date"
                  className="form-control"
                  name="ads_start_date"
                  value={form.ads_start_date}
                  onChange={handleChange}
                  min={today}
                  required
                />
              </div>

              {/* End Date */}
              <div className="col-md-6">
                <label className="form-label">End Date *</label>
                <input
                  type="date"
                  className="form-control"
                  name="ads_end_date"
                  value={form.ads_end_date}
                  onChange={handleChange}
                  min={minEndDate}
                  required
                />
              </div>

              {/* Image (only if banner) */}
              {form.type === "banner" && (
                <div className="col-md-12">
                  <label className="form-label">Image *</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => setImage(e.target.files[0])}
                    required={form.type === "banner"}
                  />
                  <div className="form-text">
                    Supported formats: JPG, JPEG, PNG, WEBP (Max 5MB)
                  </div>
                </div>
              )}

              {/* Target URL */}
              <div className="col-md-12">
                <label className="form-label">Target URL *</label>
                <input
                  type="url"
                  className="form-control"
                  name="target_url"
                  value={form.target_url}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  required
                />
              </div>

              {/* Embed code (only if embedded) */}
              {form.type === "embedded" && (
                <div className="col-md-12">
                  <label className="form-label">Embed Code *</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    name="embed_code"
                    value={form.embed_code}
                    onChange={handleChange}
                    placeholder="Paste your embed code here..."
                    required={form.type === "embedded"}
                  ></textarea>
                </div>
              )}

              {/* Terms and Conditions */}
              <div className="col-md-12">
                <div className="form-check mt-3 d-flex align-items-start">
                  <input
                    className="form-check-input me-2 mt-1"
                    type="checkbox"
                    id="termsCheck"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    required
                  />
                  <label className="form-check-label mb-0" htmlFor="termsCheck">
                    I agree to the{" "}
                    <span
                      className="text-primary fw-semibold text-decoration-underline"
                      style={{ cursor: "pointer" }}
                      onClick={handleTermsClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleTermsClick();
                        }
                      }}
                    >
                      Terms & Conditions
                    </span>
                    <span className="text-danger"> *</span>
                  </label>
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <div className="d-flex flex-wrap justify-content-start gap-2 m-0">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onHide}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!acceptedTerms}
              >
                Submit
              </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Terms and Conditions Modal */}
      <TermsAndConditionModal
        show={showTermsModal}
        onHide={handleTermsModalHide}
        onAccept={handleTermsAccept}
      />
    </>
  );
}